import { useMemo } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useGetQuestionsQuery } from '@/api/questions/getQuestions';
import { toast } from 'sonner';
import { useForcePickemScoringMutation } from '@/api/live-scoring/forcePickemScoring';
import { LiveScoringQuestion } from './LiveScoringQuestion';
import { LiveScoringProvider } from './LiveScoringContext';
import { useMatchStatsForQuestions } from '@/hooks/useMatchStatsForQuestions';

interface LiveScoringQuestionsListProps {
  pickemId: string;
}

/**
 * Reusable component that displays a list of questions with live scoring capabilities.
 * Used in both the dedicated live scoring page and the pickem editor's live scoring tab.
 */
export function LiveScoringQuestionsList({ pickemId }: LiveScoringQuestionsListProps) {
  const { data: questions = [], isLoading: questionsLoading } = useGetQuestionsQuery({
    pickem_id: pickemId || undefined,
  });

  const forceScoring = useForcePickemScoringMutation(pickemId);

  const handleUpdateScores = () => {
    forceScoring.mutate(undefined, {
      onSuccess: () => {
        toast.success('Leaderboard has been recalculated');
      },
      onError: (error: unknown) => {
        const message = error instanceof Error ? error.message : 'Failed to update scores';
        toast.error(message);
      },
    });
  };

  // Fetch match stats for all questions
  const { statsMap } = useMatchStatsForQuestions(questions);

  // Sort questions by display_order
  const sortedQuestions = useMemo(() => {
    return [...questions].sort((a, b) => {
      const orderA = a.display_order ?? 0;
      const orderB = b.display_order ?? 0;
      return orderA - orderB;
    });
  }, [questions]);

  if (questionsLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (sortedQuestions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 dark:text-gray-400">No questions found for this pickem</p>
      </div>
    );
  }

  return (
    <LiveScoringProvider pickemId={pickemId}>
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleUpdateScores}
            disabled={forceScoring.isPending}
            className="bg-green-600 hover:bg-green-700"
          >
            {forceScoring.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Update Scores
              </>
            )}
          </Button>
        </div>
        {sortedQuestions.map((question, index) => {
          // Get match stats for this question
          const questionMetadata = question.question_metadata as
            | { grading?: { sportMatchId?: string } }
            | null
            | undefined;
          const matchId = question.fixture_external_id || questionMetadata?.grading?.sportMatchId;
          const matchStats = matchId ? statsMap.get(matchId) : undefined;

          return (
            <LiveScoringQuestion
              key={question.question_id}
              question={question}
              questionNumber={index + 1}
              matchStats={matchStats}
            />
          );
        })}
      </div>
    </LiveScoringProvider>
  );
}
